import React, { useState, useEffect, useCallback, useMemo, Suspense } from 'react';
import { IonIcon } from '@ionic/react';
import {
  home, people, peopleCircle, gitNetwork, grid, map,
  settings, shield, documentText, logOut, statsChart,
  personAdd, addCircle, list, menuOutline, close,
  chevronDown, chevronForward, chevronBack, wineOutline,
  calendarOutline, peopleOutline, heartOutline, schoolOutline,
  layersOutline, homeOutline, settingsOutline, cardOutline,
} from 'ionicons/icons';
import { Redirect, Route, Switch, useHistory, useLocation } from 'react-router-dom';
import ProtectedRoute from '@/components/shared/ProtectedRoute';
import DashboardPage from '@/pages/dashboard/DashboardPage';
import MyGroupsPage from '@/pages/my-groups/MyGroupsPage';
import MemberListPage from '@/pages/members/MemberListPage';
import MemberDetailPage from '@/pages/members/MemberDetailPage';
import MemberEditPage from '@/pages/members/MemberEditPage';
import FamilyListPage from '@/pages/families/FamilyListPage';
import FamilyDetailPage from '@/pages/families/FamilyDetailPage';
import FamilyEditPage from '@/pages/families/FamilyEditPage';
import GroupListPage from '@/pages/groups/GroupListPage';
import GroupDetailPage from '@/pages/groups/GroupDetailPage';
import VisitListPage from '@/pages/visits/VisitListPage';
const MeetingEntryPage = React.lazy(() => import('@/pages/meetings/MeetingEntryPage'));
const CommunionPage = React.lazy(() => import('@/pages/communion/CommunionPage'));
const CommunionLivePage = React.lazy(() => import('@/pages/communion/CommunionLivePage'));
const CalendarPage = React.lazy(() => import('@/pages/calendar/CalendarPage'));
const EventDetailPage = React.lazy(() => import('@/pages/calendar/EventDetailPage'));
const TemplatesPage = React.lazy(() => import('@/pages/calendar/TemplatesPage'));
const SchedulingMatrixPage = React.lazy(() => import('@/pages/scheduling/SchedulingMatrixPage'));
const MySchedulePage = React.lazy(() => import('@/pages/scheduling/MySchedulePage'));
import MapPage from '@/pages/maps/MapPage';
import GeoReviewPage from '@/pages/maps/GeoReviewPage';
const RoutePlannerPage = React.lazy(() => import('@/pages/maps/RoutePlannerPage'));
import SectorListPage from '@/pages/sectors/SectorListPage';
import SectorDetailPage from '@/pages/sectors/SectorDetailPage';
const SectorBoundaryEditorPage = React.lazy(() => import('@/pages/sectors/SectorBoundaryEditorPage'));
// Grădinița Gosen — its own module, loaded only when opened.
const KgDashboardPage = React.lazy(() => import('@/pages/kindergarten/KgDashboardPage'));
const KgSettingsPage = React.lazy(() => import('@/pages/kindergarten/KgSettingsPage'));
const KgRolloverPage = React.lazy(() => import('@/pages/kindergarten/KgRolloverPage'));
const KgGroupsPage = React.lazy(() => import('@/pages/kindergarten/KgGroupsPage'));
const KgGroupDetailPage = React.lazy(() => import('@/pages/kindergarten/KgGroupDetailPage'));
const KgFamiliesPage = React.lazy(() => import('@/pages/kindergarten/KgFamiliesPage'));
const KgFamilyEditPage = React.lazy(() => import('@/pages/kindergarten/KgFamilyEditPage'));
const KgFamilyDetailPage = React.lazy(() => import('@/pages/kindergarten/KgFamilyDetailPage'));
const KgChildDetailPage = React.lazy(() => import('@/pages/kindergarten/KgChildDetailPage'));
const KgGroupLedgerPage = React.lazy(() => import('@/pages/kindergarten/KgGroupLedgerPage'));
const KgPaymentsPage = React.lazy(() => import('@/pages/kindergarten/KgPaymentsPage'));
const KgReportsPage = React.lazy(() => import('@/pages/kindergarten/KgReportsPage'));
import UserManagementPage from '@/pages/admin/UserManagementPage';
import AuditLogPage from '@/pages/admin/AuditLogPage';
import ReportsPage from '@/pages/reports/ReportsPage';
import { useAuthStore } from '@/store/authStore';
import { useLogout, useCurrentUser } from '@/hooks/useAuth';
import { usePermission } from '@/hooks/usePermission';
import { defaultLandingPath } from '@/utils/landing';
import { USER_ROLE_LABELS } from '@/utils/labels';

/* ── Navigation structure ── */
interface NavChild {
  title: string;
  path: string;
  icon: string;
  permission?: string;
}
interface NavItem {
  title: string;
  path: string;
  icon: string;
  permission?: string;
  /** Hide the entry from users who hold this permission (they have a richer route). */
  hideWhen?: string;
  children?: NavChild[];
}

const NAV_ITEMS: NavItem[] = [
  { title: 'Panou de Control', path: '/dashboard', icon: home, permission: 'dashboard.view' },
  {
    title: 'Persoane', path: '/members', icon: people, permission: 'members.view',
    children: [
      { title: 'Lista Persoane', path: '/members', icon: list, permission: 'members.view' },
      { title: 'Nebotezati / Risipitori', path: '/nebotezati', icon: heartOutline, permission: 'members.view' },
      { title: 'Adauga Persoana', path: '/members/new', icon: personAdd, permission: 'members.create' },
      { title: 'Lista Familii', path: '/families', icon: peopleCircle, permission: 'families.view' },
      { title: 'Adauga Familie', path: '/families/new', icon: addCircle, permission: 'families.create' },
    ],
  },
  { title: 'Grupuri', path: '/groups', icon: gitNetwork, permission: 'groups.view' },
  { title: 'Calendar', path: '/calendar', icon: calendarOutline, permission: 'calendar.view' },
  {
    title: 'Slujire', path: '/scheduling', icon: peopleOutline, permission: 'scheduling.view',
    children: [
      { title: 'Programare', path: '/scheduling', icon: list, permission: 'scheduling.view' },
      { title: 'Programul Meu', path: '/my-schedule', icon: peopleOutline },
    ],
  },
  { title: 'Cina Domnului', path: '/communion', icon: wineOutline, permission: 'communion.view' },
  // Live check-in volunteers hold communion.record only; everyone with the
  // matrix reaches the live screen from its "Mod live" button instead.
  { title: 'Duminica Cinei', path: '/communion/live', icon: wineOutline, permission: 'communion.record', hideWhen: 'communion.view' },
  { title: 'Vizite & Nevoi', path: '/visits', icon: documentText, permission: 'visits.view' },
  { title: 'Sectoare', path: '/sectors', icon: grid, permission: 'sectors.view' },
  { title: 'Harta', path: '/maps', icon: map, permission: 'map.view' },
  { title: 'Evidențe', path: '/reports', icon: statsChart, permission: 'dashboard.view_stats' },
  {
    title: 'Grădinița Gosen', path: '/kindergarten', icon: schoolOutline, permission: 'kindergarten.view',
    children: [
      { title: 'Panou grădiniță', path: '/kindergarten', icon: homeOutline, permission: 'kindergarten.view' },
      { title: 'Grupe', path: '/kindergarten/groups', icon: layersOutline, permission: 'kindergarten.view' },
      { title: 'Familii', path: '/kindergarten/families', icon: peopleCircle, permission: 'kindergarten.view' },
      { title: 'Plăți', path: '/kindergarten/payments', icon: cardOutline, permission: 'kindergarten.finance_view' },
      { title: 'Rapoarte', path: '/kindergarten/reports', icon: statsChart, permission: 'kindergarten.finance_view' },
      { title: 'Setări grădiniță', path: '/kindergarten/settings', icon: settingsOutline, permission: 'kindergarten.configure' },
    ],
  },
];

const ADMIN_ITEMS: NavItem[] = [
  { title: 'Setari', path: '/settings', icon: settings, permission: 'settings.view' },
  { title: 'Utilizatori', path: '/admin/users', icon: shield, permission: 'users.view' },
  { title: 'Jurnal Audit', path: '/admin/audit', icon: documentText, permission: 'audit.view' },
];

/* Route → page title mapping */
const PAGE_TITLES: Record<string, string> = {
  '/dashboard': 'Panou de Control',
  '/my-groups': 'Grupurile Mele',
  '/members': 'Persoane',
  '/nebotezati': 'Nebotezați / Fii Risipitori',
  '/members/new': 'Adauga Persoana',
  '/families': 'Familii',
  '/families/new': 'Adauga Familie',
  '/groups': 'Grupuri',
  '/calendar': 'Calendar',
  '/calendar/templates': 'Șabloane Servicii',
  '/scheduling': 'Slujire',
  '/my-schedule': 'Programul Meu',
  '/communion': 'Cina Domnului',
  '/communion/live': 'Duminica Cinei',
  '/visits': 'Vizite & Nevoi',
  '/sectors': 'Sectoare',
  '/maps': 'Harta',
  '/reports': 'Evidențe',
  '/settings': 'Setari',
  '/admin/users': 'Utilizatori',
  '/admin/audit': 'Jurnal Audit',
  '/kindergarten': 'Grădinița Gosen',
  '/kindergarten/groups': 'Grupe',
  '/kindergarten/families': 'Familii grădiniță',
  '/kindergarten/families/new': 'Adaugă familie',
  '/kindergarten/payments': 'Plăți grădiniță',
  '/kindergarten/reports': 'Rapoarte grădiniță',
  '/kindergarten/settings': 'Setări grădiniță',
  '/kindergarten/settings/rollover': 'Pornire an școlar',
};

function getPageTitle(pathname: string): string {
  // Exact match first
  if (PAGE_TITLES[pathname]) return PAGE_TITLES[pathname];
  // Pattern matches
  if (/^\/members\/[^/]+\/edit$/.test(pathname)) return 'Editare Persoana';
  if (/^\/members\/[^/]+$/.test(pathname)) return 'Detalii Persoana';
  if (/^\/families\/[^/]+\/edit$/.test(pathname)) return 'Editare Familie';
  if (/^\/families\/[^/]+$/.test(pathname)) return 'Detalii Familie';
  if (/^\/groups\/[^/]+$/.test(pathname)) return 'Detalii Grup';
  if (/^\/calendar\/[^/]+$/.test(pathname)) return 'Eveniment';
  if (/^\/sectors\/[^/]+$/.test(pathname)) return 'Detalii Sector';
  if (/^\/kindergarten\/groups\/[^/]+\/ledger$/.test(pathname)) return 'Situație plăți grupă';
  if (/^\/kindergarten\/groups\/[^/]+$/.test(pathname)) return 'Detalii Grupă';
  if (/^\/kindergarten\/families\/[^/]+\/edit$/.test(pathname)) return 'Editare familie';
  if (/^\/kindergarten\/families\/[^/]+$/.test(pathname)) return 'Detalii familie';
  if (/^\/kindergarten\/children\/[^/]+$/.test(pathname)) return 'Detalii copil';
  return 'Betel Gestiune';
}

const AppShell: React.FC = () => {
  const history = useHistory();
  const location = useLocation();
  const authStore = useAuthStore();
  const { user } = authStore;
  const logoutMutation = useLogout();
  useCurrentUser();
  const { can, canAny } = usePermission();
  const showAdmin = canAny(['users.view', 'audit.view', 'settings.view']);
  const defaultLanding = defaultLandingPath(can);
  const visibleNavItems = useMemo(
    () => NAV_ITEMS.filter(
      (item) => (!item.permission || can(item.permission)) && !(item.hideWhen && can(item.hideWhen)),
    ),
    [can],
  );
  const visibleAdminItems = useMemo(
    () => ADMIN_ITEMS.filter((item) => !item.permission || can(item.permission)),
    [can],
  );

  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [expandedSection, setExpandedSection] = useState<string | null>(null);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  useEffect(() => {
    if (location.pathname.startsWith('/members') || location.pathname.startsWith('/families')) {
      setExpandedSection('/members');
    } else if (location.pathname.startsWith('/scheduling') || location.pathname.startsWith('/my-schedule')) {
      setExpandedSection('/scheduling');
    } else if (location.pathname.startsWith('/kindergarten')) {
      setExpandedSection('/kindergarten');
    }
  }, [location.pathname]);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [mobileMenuOpen]);

  const pageTitle = useMemo(() => getPageTitle(location.pathname), [location.pathname]);

  const isActive = useCallback((path: string) => {
    if (path === '/dashboard') return location.pathname === '/dashboard' || location.pathname === '/';
    if (path === '/members') return location.pathname.startsWith('/members') || location.pathname.startsWith('/families');
    if (path === '/scheduling') return location.pathname.startsWith('/scheduling') || location.pathname.startsWith('/my-schedule');
    return location.pathname.startsWith(path);
  }, [location.pathname]);

  /** Navigate to a path. Only close the mobile drawer if closeDrawer=true */
  const navigate = useCallback((path: string, closeDrawer = true) => {
    if (location.pathname !== path) {
      history.push(path);
    }
    if (closeDrawer) {
      setMobileMenuOpen(false);
    }
  }, [location.pathname, history]);

  const handleLogout = () => {
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('tenant_slug');
    localStorage.removeItem('tenant_name');
    authStore.logout();
    logoutMutation.mutate();
  };

  const userInitials = user
    ? `${(user.first_name?.[0] ?? '').toUpperCase()}${(user.last_name?.[0] ?? '').toUpperCase()}`
    : '?';

  const userDisplayName = user
    ? `${user.first_name ?? ''} ${user.last_name ?? ''}`.trim() || user.name || 'Utilizator'
    : 'Utilizator';

  const userRole = user?.role ? (USER_ROLE_LABELS[user.role] ?? user.role.replace('_', ' ')) : '';

  /** Render nav items — used in both desktop sidebar and mobile drawer */
  const renderNavItems = (closeMobileOnLeafClick: boolean) => (
    <nav className="sidebar__nav">
      <div className="sidebar__section-label">MENIU</div>
      {visibleNavItems.map((item) => {
        const active = isActive(item.path);
        const hasChildren = !!item.children;
        const isExpanded = expandedSection === item.path || (active && hasChildren);

        return (
          <div key={item.path}>
            <button
              className={`sidebar__link${active ? ' sidebar__link--active' : ''}`}
              onClick={() => {
                if (hasChildren) {
                  // Toggle expand/collapse — do NOT navigate or close drawer
                  setExpandedSection(isExpanded ? null : item.path);
                } else {
                  // Leaf item: navigate and close drawer
                  navigate(item.path, closeMobileOnLeafClick);
                }
              }}
            >
              <IonIcon icon={item.icon} className="sidebar__link-icon" />
              <span className="sidebar__link-text">{item.title}</span>
              {hasChildren && (
                <IonIcon
                  icon={isExpanded ? chevronDown : chevronForward}
                  className="sidebar__link-chevron"
                />
              )}
            </button>
            {hasChildren && isExpanded && (
              <div className="sidebar__children">
                {item.children!.filter((child) => !child.permission || can(child.permission)).map((child) => (
                  <button
                    key={child.path}
                    className={`sidebar__child${
                      child.path.endsWith('/new')
                        ? location.pathname === child.path ? ' sidebar__child--active' : ''
                        : location.pathname === child.path || (location.pathname.startsWith(child.path + '/') && !location.pathname.endsWith('/new'))
                          ? ' sidebar__child--active' : ''
                    }`}
                    onClick={() => navigate(child.path, closeMobileOnLeafClick)}
                  >
                    <IonIcon icon={child.icon} className="sidebar__child-icon" />
                    <span>{child.title}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        );
      })}

      {showAdmin && (
        <>
          <div className="sidebar__section-label">ADMINISTRARE</div>
          {visibleAdminItems.map((item) => (
            <button
              key={item.path}
              className={`sidebar__link${isActive(item.path) ? ' sidebar__link--active' : ''}`}
              onClick={() => navigate(item.path, closeMobileOnLeafClick)}
            >
              <IonIcon icon={item.icon} className="sidebar__link-icon" />
              <span className="sidebar__link-text">{item.title}</span>
            </button>
          ))}
        </>
      )}
    </nav>
  );

  return (
    <div className="app-layout">
      {/* ── Mobile top bar — back button on deep routes, hamburger otherwise ── */}
      <header className="mobile-header">
        {(() => {
          const segs = location.pathname.split('/').filter(Boolean);
          // /communion/live is a top-level screen (a volunteer's only one), not a detail page.
          const isDeepRoute = (segs.length > 1 || segs.includes('new')) && location.pathname !== '/communion/live';
          if (isDeepRoute) {
            return (
              <button
                className="mobile-header__hamburger"
                onClick={() => history.length > 1 ? history.goBack() : navigate(defaultLanding)}
                aria-label="Înapoi"
              >
                <IonIcon icon={chevronBack} />
              </button>
            );
          }
          return (
            <button
              className="mobile-header__hamburger"
              onClick={() => setMobileMenuOpen(true)}
              aria-label="Meniu"
            >
              <IonIcon icon={menuOutline} />
            </button>
          );
        })()}
        <span className="mobile-header__title">{pageTitle}</span>
        <div className="mobile-header__spacer" />
      </header>

      {/* ── Mobile sidebar drawer (overlay) ── */}
      {mobileMenuOpen && (
        <div className="mobile-drawer-overlay" onClick={() => setMobileMenuOpen(false)}>
          <aside className="mobile-drawer" onClick={(e) => e.stopPropagation()}>
            <button className="mobile-drawer__close" onClick={() => setMobileMenuOpen(false)}>
              <IonIcon icon={close} />
            </button>

            {/* Brand */}
            <div className="sidebar__brand" onClick={() => navigate(defaultLanding, true)}>
              <div className="sidebar__brand-icon">
                <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                </svg>
              </div>
              <span className="sidebar__brand-name">Betel Gestiune</span>
            </div>

            {renderNavItems(true)}

            {/* User card + Logout */}
            <div className="sidebar__footer">
              <div className="sidebar__user">
                <div className="sidebar__user-avatar">{userInitials}</div>
                <div className="sidebar__user-info">
                  <div className="sidebar__user-name">{userDisplayName}</div>
                  {userRole && <div className="sidebar__user-role">{userRole}</div>}
                </div>
              </div>
              <button className="sidebar__logout" onClick={() => { setMobileMenuOpen(false); setShowLogoutConfirm(true); }}>
                <IonIcon icon={logOut} />
                <span>Deconectare</span>
              </button>
            </div>
          </aside>
        </div>
      )}

      {/* ── Desktop Sidebar ── */}
      <aside className={`sidebar sidebar--desktop${sidebarCollapsed ? ' sidebar--collapsed' : ''}`}>
        {/* Brand */}
        <div className="sidebar__brand" onClick={() => navigate(defaultLanding)}>
          <div className="sidebar__brand-icon">
            <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
            </svg>
          </div>
          {!sidebarCollapsed && <span className="sidebar__brand-name">Betel Gestiune</span>}
        </div>

        {!sidebarCollapsed && renderNavItems(false)}

        {/* Collapsed: icon-only nav */}
        {sidebarCollapsed && (
          <nav className="sidebar__nav sidebar__nav--collapsed">
            {visibleNavItems.filter((i) => !i.children).map((item) => (
              <button
                key={item.path}
                className={`sidebar__link sidebar__link--icon-only${isActive(item.path) ? ' sidebar__link--active' : ''}`}
                onClick={() => navigate(item.path)}
                title={item.title}
              >
                <IonIcon icon={item.icon} className="sidebar__link-icon" />
              </button>
            ))}
            {/* Show child items as top-level icons when collapsed */}
            {visibleNavItems.filter((i) => i.children).flatMap((i) => i.children!).filter((c) => !c.path.endsWith('/new') && (!c.permission || can(c.permission))).map((child) => (
              <button
                key={child.path}
                className={`sidebar__link sidebar__link--icon-only${location.pathname.startsWith(child.path) ? ' sidebar__link--active' : ''}`}
                onClick={() => navigate(child.path)}
                title={child.title}
              >
                <IonIcon icon={child.icon} className="sidebar__link-icon" />
              </button>
            ))}
            {showAdmin && visibleAdminItems.map((item) => (
              <button
                key={item.path}
                className={`sidebar__link sidebar__link--icon-only${isActive(item.path) ? ' sidebar__link--active' : ''}`}
                onClick={() => navigate(item.path)}
                title={item.title}
              >
                <IonIcon icon={item.icon} className="sidebar__link-icon" />
              </button>
            ))}
          </nav>
        )}

        {/* Collapse toggle */}
        <button
          className="sidebar__collapse-btn"
          onClick={() => setSidebarCollapsed((s) => !s)}
          title={sidebarCollapsed ? 'Extinde meniul' : 'Restrânge meniul'}
        >
          <IonIcon icon={chevronBack} style={{ transform: sidebarCollapsed ? 'rotate(180deg)' : 'none', transition: 'transform .2s' }} />
        </button>

        {/* User card + Logout */}
        <div className="sidebar__footer">
          {sidebarCollapsed ? (
            <>
              <div className="sidebar__user-avatar" style={{ margin: '0 auto' }} title={userDisplayName}>{userInitials}</div>
              <button className="sidebar__logout sidebar__logout--icon-only" onClick={() => setShowLogoutConfirm(true)} title="Deconectare">
                <IonIcon icon={logOut} />
              </button>
            </>
          ) : (
            <>
              <div className="sidebar__user">
                <div className="sidebar__user-avatar">{userInitials}</div>
                <div className="sidebar__user-info">
                  <div className="sidebar__user-name">{userDisplayName}</div>
                  {userRole && <div className="sidebar__user-role">{userRole}</div>}
                </div>
              </div>
              <button className="sidebar__logout" onClick={() => setShowLogoutConfirm(true)}>
                <IonIcon icon={logOut} />
                <span>Deconectare</span>
              </button>
            </>
          )}
        </div>
      </aside>

      {/* ── Main content area ── */}
      <main className="app-main">
        <Suspense fallback={<div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}><span className="login-spinner" style={{ width: 24, height: 24, borderWidth: 2 }} /></div>}>
        <Switch>
          <ProtectedRoute exact path="/dashboard" component={DashboardPage} requiredPermissions={['dashboard.view']} />
          <ProtectedRoute exact path="/my-groups" component={MyGroupsPage} />
          <ProtectedRoute exact path="/members" component={MemberListPage} requiredPermissions={['members.view']} />
          {/* Same table, different population — see MemberListPage's outreach prop. */}
          <ProtectedRoute exact path="/nebotezati" requiredPermissions={['members.view']} render={() => <MemberListPage outreach />} />
          <ProtectedRoute exact path="/members/new" component={MemberEditPage} requiredPermissions={['members.create']} />
          <ProtectedRoute exact path="/members/:id/edit" component={MemberEditPage} requiredPermissions={['members.edit', 'members.edit_spiritual']} />
          <ProtectedRoute exact path="/members/:id" component={MemberDetailPage} requiredPermissions={['members.view']} />
          <ProtectedRoute exact path="/families" component={FamilyListPage} />
          <ProtectedRoute exact path="/families/new" component={FamilyEditPage} requiredPermissions={['families.create']} />
          <ProtectedRoute exact path="/families/:id/edit" component={FamilyEditPage} requiredPermissions={['families.edit']} />
          <ProtectedRoute exact path="/families/:id" component={FamilyDetailPage} />
          <ProtectedRoute exact path="/groups" component={GroupListPage} />
          <ProtectedRoute exact path="/groups/:id" component={GroupDetailPage} />
          <ProtectedRoute exact path="/calendar" component={CalendarPage} requiredPermissions={['calendar.view']} />
          <ProtectedRoute exact path="/calendar/templates" component={TemplatesPage} requiredPermissions={['calendar.configure']} />
          <ProtectedRoute exact path="/calendar/:id" component={EventDetailPage} requiredPermissions={['calendar.view']} />
          <ProtectedRoute exact path="/scheduling" component={SchedulingMatrixPage} requiredPermissions={['scheduling.view']} />
          <ProtectedRoute exact path="/my-schedule" component={MySchedulePage} />
          <ProtectedRoute exact path="/meetings/:id" component={MeetingEntryPage} requiredPermissions={['attendance.view']} />
          <ProtectedRoute exact path="/communion" component={CommunionPage} requiredPermissions={['communion.view']} />
          <ProtectedRoute exact path="/communion/live" component={CommunionLivePage} requiredPermissions={['communion.record']} />
          <ProtectedRoute exact path="/visits" component={VisitListPage} />
          <ProtectedRoute exact path="/maps" component={MapPage} />
          <ProtectedRoute exact path="/maps/review" component={GeoReviewPage} />
          <ProtectedRoute exact path="/maps/route-planner" component={RoutePlannerPage} />
          <ProtectedRoute exact path="/sectors" component={SectorListPage} />
          <ProtectedRoute exact path="/sectors/:id/boundary" component={SectorBoundaryEditorPage} requiredPermissions={['sectors.edit']} />
          <ProtectedRoute exact path="/sectors/:id" component={SectorDetailPage} />
          <ProtectedRoute exact path="/reports" component={ReportsPage} />
          {/* Grădinița Gosen — static paths before :id ones */}
          <ProtectedRoute exact path="/kindergarten" component={KgDashboardPage} requiredPermissions={['kindergarten.view']} />
          <ProtectedRoute exact path="/kindergarten/settings/rollover" component={KgRolloverPage} requiredPermissions={['kindergarten.configure']} />
          <ProtectedRoute exact path="/kindergarten/settings" component={KgSettingsPage} requiredPermissions={['kindergarten.configure']} />
          <ProtectedRoute exact path="/kindergarten/groups" component={KgGroupsPage} requiredPermissions={['kindergarten.view']} />
          <ProtectedRoute exact path="/kindergarten/groups/:id/ledger" component={KgGroupLedgerPage} requiredPermissions={['kindergarten.finance_view']} />
          <ProtectedRoute exact path="/kindergarten/groups/:id" component={KgGroupDetailPage} requiredPermissions={['kindergarten.view']} />
          <ProtectedRoute exact path="/kindergarten/payments" component={KgPaymentsPage} requiredPermissions={['kindergarten.finance_view']} />
          <ProtectedRoute exact path="/kindergarten/reports" component={KgReportsPage} requiredPermissions={['kindergarten.finance_view']} />
          <ProtectedRoute exact path="/kindergarten/families" component={KgFamiliesPage} requiredPermissions={['kindergarten.view']} />
          <ProtectedRoute exact path="/kindergarten/families/new" component={KgFamilyEditPage} requiredPermissions={['kindergarten.manage']} />
          <ProtectedRoute exact path="/kindergarten/families/:id/edit" component={KgFamilyEditPage} requiredPermissions={['kindergarten.manage']} />
          <ProtectedRoute exact path="/kindergarten/families/:id" component={KgFamilyDetailPage} requiredPermissions={['kindergarten.view']} />
          <ProtectedRoute exact path="/kindergarten/children/:id" component={KgChildDetailPage} requiredPermissions={['kindergarten.view']} />
          <ProtectedRoute exact path="/settings" component={DashboardPage} requiredPermissions={['settings.view']} />
          <ProtectedRoute exact path="/admin/users" component={UserManagementPage} requiredPermissions={['users.view']} />
          <ProtectedRoute exact path="/admin/audit" component={AuditLogPage} requiredPermissions={['audit.view']} />
          {/* Wait for /auth/me: before permissions load every user looks like a
              nobody and would be sent to /my-groups on a plain refresh. */}
          <Route exact path="/">{user ? <Redirect to={defaultLanding} /> : null}</Route>
        </Switch>
        </Suspense>
      </main>


      {/* ── Logout confirmation dialog ── */}
      {showLogoutConfirm && (
        <div className="logout-overlay" onClick={() => setShowLogoutConfirm(false)}>
          <div className="logout-dialog" onClick={(e) => e.stopPropagation()}>
            <div className="logout-dialog__icon">
              <IonIcon icon={logOut} />
            </div>
            <h3 className="logout-dialog__title">Deconectare</h3>
            <p className="logout-dialog__text">Esti sigur ca vrei sa te deconectezi?</p>
            <div className="logout-dialog__actions">
              <button className="form-btn form-btn--secondary" onClick={() => setShowLogoutConfirm(false)}>
                Anuleaza
              </button>
              <button className="form-btn form-btn--danger" onClick={handleLogout}>
                Deconectare
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AppShell;
